import { Component, inject, Output, EventEmitter, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { ZaxbotComponent } from './zaxbot.component';

type WizardState = 'mandatory-email' | 'intro' | 'generating' | 'scan' | 'verifying' | 'success';

@Component({
  selector: 'app-mfa-wizard',
  standalone: true,
  imports: [CommonModule, FormsModule, ZaxbotComponent],
  template: `
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md">
      <!-- Retro Windows 95 Style Modal -->
      <!-- overflow-visible + mt para dejar espacio al bot que asoma por arriba -->
      <div class="relative overflow-visible bg-[#c0c0c0] text-black w-full max-w-lg border-t-2 border-l-2 border-white border-b-2 border-r-2 border-gray-800 shadow-[4px_4px_0_0_rgba(0,0,0,0.5)] flex flex-col font-mono mt-20">
        
        <!-- ZAX-BOT: cabeza y antena visibles, cuerpo oculto detrás del panel -->
        <div class="absolute -top-[155px] left-1/2 -translate-x-1/2 z-0">
          <app-zaxbot
            [mode]="state === 'scan' ? 'tracking' : 'idle'"
            [digitCount]="mfaCodeInput.length"
            [success]="state === 'success'"
          ></app-zaxbot>
        </div>

        <!-- Title Bar: z-[1] para tapar el cuerpo del bot -->
        <div class="relative z-[1] bg-blue-800 text-white flex justify-between items-center px-2 py-1 select-none">
          <div class="flex items-center gap-2 font-bold text-sm">
            <i class="ph-fill ph-terminal-window"></i>
            <span>MFA_ASSISTANT.EXE</span>
          </div>
          <button *ngIf="!enforceEmailVerify" (click)="close()" class="bg-[#c0c0c0] text-black w-5 h-5 flex items-center justify-center border-t border-l border-white border-b border-r border-gray-800 font-bold hover:active:border-t-gray-800 hover:active:border-l-gray-800 hover:active:border-b-white hover:active:border-r-white text-xs">
            X
          </button>
        </div>

        <!-- Content Area -->
        <div class="px-6 pt-4 pb-6 flex flex-col items-center justify-center min-h-[300px] relative">
          
          <!-- Speech Bubble centrada con cola apuntando al bot arriba -->
          <div class="relative bg-white border-2 border-black p-3 shadow-[2px_2px_0_0_rgba(0,0,0,1)] w-full mb-6 mt-2">
            <!-- Cola apuntando hacia arriba (al bot) -->
            <div class="absolute -top-[14px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[12px] border-b-black"></div>
            <div class="absolute -top-[10px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[10px] border-b-white"></div>
            
            <p class="text-sm leading-relaxed font-bold text-center">{{ getDialogText() }}</p>
          </div>

          <!-- Dynamic Body based on state -->
          <div class="w-full flex-1 flex flex-col items-center">
            
            <ng-container *ngIf="state === 'mandatory-email'">
              <div class="flex flex-col gap-2 mt-2 w-full">
                <button 
                  (click)="resendEmail()"
                  [disabled]="resendBlocked"
                  [class.opacity-50]="resendBlocked"
                  [class.cursor-not-allowed]="resendBlocked"
                  class="bg-[#c0c0c0] px-6 py-2 border-t-2 border-l-2 border-white border-b-2 border-r-2 border-gray-800 font-bold shadow-sm active:border-t-gray-800 active:border-l-gray-800 active:border-b-white active:border-r-white tracking-widest text-sm hover:bg-gray-300 transition-colors w-full flex items-center justify-center gap-2"
                >
                  <i *ngIf="resendBlocked" class="ph-bold ph-timer"></i>
                  {{ resendBlocked ? 'Espera ' + formattedCountdown : 'Re-enviar Enlace' }}
                </button>
                <button 
                  (click)="checkEmailVerified()"
                  class="bg-blue-800 text-white px-6 py-2 border-t-2 border-l-2 border-blue-400 border-b-2 border-r-2 border-blue-900 font-bold shadow-sm active:border-t-blue-900 active:border-l-blue-900 active:border-b-blue-400 active:border-r-blue-400 tracking-widest text-sm hover:bg-blue-700 transition-colors w-full flex items-center justify-center gap-2"
                >
                  <i *ngIf="isReloading" class="ph ph-spinner animate-spin"></i>
                  Ya lo verifiqué (Actualizar Sesión)
                </button>
              </div>
            </ng-container>

            <ng-container *ngIf="state === 'intro'">
              <button 
                (click)="startEnrollment()"
                class="mt-4 bg-[#c0c0c0] px-6 py-2 border-t-2 border-l-2 border-white border-b-2 border-r-2 border-gray-800 font-bold shadow-sm active:border-t-gray-800 active:border-l-gray-800 active:border-b-white active:border-r-white uppercase tracking-widest text-sm hover:bg-gray-300 transition-colors"
              >
                Configurar Ahora
              </button>
            </ng-container>

            <ng-container *ngIf="state === 'generating'">
              <div class="flex items-center justify-center p-4">
                <!-- Retro loading bar -->
                <div class="w-full max-w-xs border-2 border-gray-800 h-4 bg-white p-[2px] flex relative">
                  <div class="bg-blue-800 h-full w-full animate-[progress_2s_ease-in-out_infinite]"></div>
                </div>
              </div>
            </ng-container>

            <ng-container *ngIf="state === 'scan'">
              <div class="bg-white p-2 border-2 border-black mb-4">
                <img [src]="mfaQrCodeUrl" alt="QR Code" class="w-40 h-40 rendering-pixelated" />
              </div>
              <div class="w-full max-w-[200px] mb-2">
                <label class="block text-xs font-bold mb-1">Código de 6 dígitos:</label>
                <input 
                  type="text" 
                  [(ngModel)]="mfaCodeInput"
                  placeholder="000 000"
                  maxlength="6"
                  class="w-full bg-white border-2 border-gray-800 px-2 py-2 text-center text-lg font-bold tracking-[0.2em] focus:outline-none focus:bg-yellow-100"
                >
              </div>
              <button 
                (click)="verifyCode()"
                [disabled]="mfaCodeInput.length !== 6"
                class="bg-[#c0c0c0] px-6 py-2 border-t-2 border-l-2 border-white border-b-2 border-r-2 border-gray-800 font-bold shadow-sm active:border-t-gray-800 active:border-l-gray-800 active:border-b-white active:border-r-white disabled:opacity-50 uppercase tracking-widest text-sm"
              >
                Verificar
              </button>
            </ng-container>

            <ng-container *ngIf="state === 'verifying'">
               <div class="flex items-center gap-2 mt-4 text-sm font-bold animate-pulse">
                 <i class="ph ph-spinner animate-spin"></i>
                 <span>Comprobando la red Zaxis...</span>
               </div>
            </ng-container>

          </div>
        </div>
        
        <!-- Status Bar -->
        <div class="bg-[#c0c0c0] border-t border-white px-2 py-1 text-[10px] text-gray-600 flex justify-between">
          <span>Sistema Zaxis v2.0</span>
          <span>ESTADO: {{ state | uppercase }}</span>
        </div>

      </div>
    </div>
  `,
  styles: [`
    @keyframes progress {
      0% { width: 0%; }
      50% { width: 100%; }
      100% { width: 0%; }
    }
  `]
})
export class MfaWizardComponent implements OnInit {
  @Input() enforceEmailVerify = false;
  @Output() onComplete = new EventEmitter<void>();
  @Output() onSkip = new EventEmitter<void>();

  authService = inject(AuthService);

  state: WizardState = 'intro';
  mfaSecretObject: any = null;
  mfaQrCodeUrl: string = '';
  mfaCodeInput: string = '';
  errorMessage: string = '';
  isReloading = false;

  ngOnInit() {
    if (this.enforceEmailVerify) {
      this.state = 'mandatory-email';
      this.checkRateLimit();
      if (!this.resendBlocked) {
        // Auto-send first time without showing error
        this.autoSendEmail();
      }
    }
  }

  resendBlocked = false;
  resendCountdown = 0;
  private countdownInterval: any;

  private getRetryData() {
    if (typeof window === 'undefined') return { count: 0, lastTime: 0 };
    try {
      const data = JSON.parse(localStorage.getItem('zaxis_email_resend') || '{}');
      return { count: data.count || 0, lastTime: data.lastTime || 0 };
    } catch {
      return { count: 0, lastTime: 0 };
    }
  }

  private saveRetryData(count: number, lastTime: number) {
    if (typeof window !== 'undefined') {
      localStorage.setItem('zaxis_email_resend', JSON.stringify({ count, lastTime }));
    }
  }

  private checkRateLimit() {
    const { count, lastTime } = this.getRetryData();
    if (count === 0) return;

    let blockDuration = 0;
    if (count === 1) blockDuration = 60 * 1000; // 1 minute
    else if (count === 2) blockDuration = 5 * 60 * 1000; // 5 minutes
    else blockDuration = 60 * 60 * 1000; // 1 hour

    const now = Date.now();
    const timePassed = now - lastTime;

    if (timePassed < blockDuration) {
      this.resendBlocked = true;
      this.resendCountdown = Math.ceil((blockDuration - timePassed) / 1000);
      this.startCountdown();
    }
  }

  private startCountdown() {
    if (this.countdownInterval) clearInterval(this.countdownInterval);
    this.countdownInterval = setInterval(() => {
      this.resendCountdown--;
      if (this.resendCountdown <= 0) {
        this.resendBlocked = false;
        clearInterval(this.countdownInterval);
      }
    }, 1000);
  }

  get formattedCountdown(): string {
    if (this.resendCountdown < 60) return `${this.resendCountdown}s`;
    const m = Math.floor(this.resendCountdown / 60);
    const s = this.resendCountdown % 60;
    if (m < 60) return `${m}:${s.toString().padStart(2, '0')}`;
    const h = Math.floor(m / 60);
    const mRemaining = m % 60;
    return `${h}:${mRemaining.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  async autoSendEmail() {
    try {
      await this.authService.sendVerificationEmail();
      let { count } = this.getRetryData();
      count = count === 0 ? 1 : count; // Mark first send
      this.saveRetryData(count, Date.now());
      this.checkRateLimit();
    } catch (e) {
      console.error('Auto-send failed:', e);
    }
  }

  getDialogText(): string {
    if (this.errorMessage) return this.errorMessage;

    switch (this.state) {
      case 'mandatory-email': return '¡ALTO AHÍ! El servidor central detecta que tu correo no está verificado. Por protocolos de seguridad estricta, tienes el acceso restringido hasta que confirmes el correo. Se te ha enviado un enlace a tu bandeja de entrada.';
      case 'intro': return 'Hola, soy ZAX-BOT. Veo que aún no tienes activado el Doble Factor (2FA). Es crucial para mantener la telemetría segura. ¿Lo configuramos?';
      case 'generating': return 'Solicitando llaves criptográficas a los servidores centrales...';
      case 'scan': return '¡Listo! Escanea este patrón con tu Authenticator o Samsung Wallet y escribe el código abajo.';
      case 'verifying': return 'Un momento, estoy validando la firma...';
      case 'success': return '¡Excelente trabajo! Tu conexión ahora es blindada. Cerrando asistente...';
      default: return '...';
    }
  }

  async resendEmail() {
    if (this.resendBlocked) return;

    try {
      await this.authService.sendVerificationEmail();
      this.errorMessage = '¡Correo re-enviado exitosamente! Revisa tu bandeja.';

      let { count } = this.getRetryData();
      count++;
      this.saveRetryData(count, Date.now());
      this.checkRateLimit();

      setTimeout(() => this.errorMessage = '', 3000);
    } catch (e: any) {
      this.errorMessage = 'Error enviando correo: ' + e.message;
    }
  }

  async checkEmailVerified() {
    if (this.isReloading) return;
    this.isReloading = true;
    this.errorMessage = '';

    try {
      const verified = await this.authService.reloadAuthToken();
      if (verified) {
        // Exito
        this.enforceEmailVerify = false;

        // Check if MFA is already enrolled (just in case they verified in another session)
        if (this.authService.isMfaEnrolled()) {
          this.onComplete.emit();
        } else {
          this.state = 'intro'; // proceed to optional MFA
        }
      } else {
        this.errorMessage = 'Aún no está verificado en el sistema. Asegúrate de hacer clic en el enlace del correo y vuelve a intentar.';
      }
    } catch (e: any) {
      this.errorMessage = 'Error conectando con BFF: ' + e.message;
    } finally {
      this.isReloading = false;
    }
  }

  async startEnrollment() {
    this.errorMessage = '';
    this.state = 'generating';

    try {
      this.mfaSecretObject = await this.authService.generateTotpSecret();
      const u = this.authService.getAuthInstance().currentUser;
      const accountName = u?.email || 'Admin';
      const qrData = this.mfaSecretObject.generateQrCodeUrl(accountName, 'Kairos-Zaxis');
      this.mfaQrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrData)}`;

      // Simulate loading for the retro effect
      setTimeout(() => {
        this.state = 'scan';
      }, 2000);
    } catch (err: any) {
      console.error(err);
      if (err.code === 'auth/unverified-email') {
        this.state = 'mandatory-email';
        this.enforceEmailVerify = true;
        this.errorMessage = '¡ALERTA! Tu email no está verificado. Te acabo de enviar un enlace a tu bandeja. Confírmalo y vuelve a intentarlo.';
        try {
          await this.authService.sendVerificationEmail();
        } catch (e) { console.error('Error enviando email', e); }
      } else if (err.code === 'auth/requires-recent-login') {
        this.errorMessage = 'Tu sesión expiró. Por seguridad, debes volver a iniciar sesión para activar el 2FA.';
        setTimeout(() => this.authService.logout(), 3000);
        this.state = 'intro';
      } else {
        this.errorMessage = 'Ocurrió un error inesperado. Inténtalo de nuevo.';
        this.state = 'intro';
      }
    }
  }

  async verifyCode() {
    if (this.mfaCodeInput.length !== 6) return;

    this.errorMessage = '';
    this.state = 'verifying';

    try {
      await this.authService.enrollTotp(this.mfaSecretObject, this.mfaCodeInput.trim());
      this.state = 'success';
      setTimeout(() => {
        this.onComplete.emit();
      }, 3000); // Let them read the success message
    } catch (err: any) {
      console.error(err);
      if (err.code === 'auth/requires-recent-login') {
        this.errorMessage = 'Tu sesión expiró. Por seguridad, debes volver a iniciar sesión para activar el 2FA.';
        setTimeout(() => this.authService.logout(), 3000);
      } else {
        this.errorMessage = '¡Código incorrecto o expirado! Vuelve a intentarlo.';
      }
      this.mfaCodeInput = '';
      this.state = 'scan';
    }
  }

  close() {
    this.onSkip.emit();
  }
}
